import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  academyService,
  AcademyPaymentRequest,
  UpgradeCalculationRequest,
  UpgradePaymentRequest,
} from '../../services/academy.service';
import type { AcademySubscriptionsResponse, AcademyStatsResponse, SubscriptionFilters } from '@/types/academy.types';

// Academy state interface
export interface AcademyState {
  // Subscriptions list
  subscriptions: AcademySubscriptionsResponse | null;
  subscriptionsLoading: boolean;
  subscriptionsError: string | null;

  // Statistics
  stats: AcademyStatsResponse | null;
  statsLoading: boolean;
  statsError: string | null;

  // Payment link generation
  paymentLink: {
    authorizationUrl: string;
    reference: string;
    amount: number;
    metadata: any;
  } | null;
  paymentLinkLoading: boolean;
  paymentLinkError: string | null;

  // Upgrade calculation
  upgradeCalculation: {
    premiumMonthlyRate: number;
    academyMonthlyRate: number;
    monthsPaid: number;
    premiumPaid: number;
    academyRequired: number;
    balanceDue: number;
  } | null;
  calculationLoading: boolean;
  calculationError: string | null;

  // Upgrade payment link
  upgradePaymentLink: {
    authorizationUrl: string;
    reference: string;
    amount: number;
    metadata: any;
  } | null;
  upgradePaymentLinkLoading: boolean;
  upgradePaymentLinkError: string | null;
}

const initialState: AcademyState = {
  subscriptions: null,
  subscriptionsLoading: false,
  subscriptionsError: null,

  stats: null,
  statsLoading: false,
  statsError: null,

  paymentLink: null,
  paymentLinkLoading: false,
  paymentLinkError: null,

  upgradeCalculation: null,
  calculationLoading: false,
  calculationError: null,

  upgradePaymentLink: null,
  upgradePaymentLinkLoading: false,
  upgradePaymentLinkError: null,
};

// Async thunks
export const fetchAcademySubscriptions = createAsyncThunk(
  'academy/fetchSubscriptions',
  async (filters: SubscriptionFilters | undefined, { rejectWithValue }) => {
    try {
      const response = await academyService.getSubscriptions(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error?.message || error.message || 'Failed to fetch subscriptions'
      );
    }
  }
);

export const fetchAcademyStats = createAsyncThunk(
  'academy/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await academyService.getStats();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error?.message || error.message || 'Failed to fetch statistics'
      );
    }
  }
);

export const generateAcademyPaymentLink = createAsyncThunk(
  'academy/generatePaymentLink',
  async (data: AcademyPaymentRequest, { rejectWithValue }) => {
    try {
      const response = await academyService.generatePaymentLink(data);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error?.message || error.message || 'Failed to generate payment link'
      );
    }
  }
);

export const calculateAcademyUpgrade = createAsyncThunk(
  'academy/calculateUpgrade',
  async (data: UpgradeCalculationRequest, { rejectWithValue }) => {
    try {
      const response = await academyService.calculateUpgrade(data);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error?.message || error.message || 'Failed to calculate upgrade'
      );
    }
  }
);

export const generateAcademyUpgradeLink = createAsyncThunk(
  'academy/generateUpgradeLink',
  async (data: UpgradePaymentRequest, { rejectWithValue }) => {
    try {
      const response = await academyService.generateUpgradeLink(data);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error?.message || error.message || 'Failed to generate upgrade link'
      );
    }
  }
);

// Slice
const academySlice = createSlice({
  name: 'academy',
  initialState,
  reducers: {
    clearPaymentLink: (state) => {
      state.paymentLink = null;
      state.paymentLinkError = null;
    },
    clearUpgradeCalculation: (state) => {
      state.upgradeCalculation = null;
      state.calculationError = null;
    },
    clearUpgradePaymentLink: (state) => {
      state.upgradePaymentLink = null;
      state.upgradePaymentLinkError = null;
    },
    resetAcademyState: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch subscriptions
    builder
      .addCase(fetchAcademySubscriptions.pending, (state) => {
        state.subscriptionsLoading = true;
        state.subscriptionsError = null;
      })
      .addCase(fetchAcademySubscriptions.fulfilled, (state, action) => {
        state.subscriptionsLoading = false;
        state.subscriptions = action.payload;
      })
      .addCase(fetchAcademySubscriptions.rejected, (state, action) => {
        state.subscriptionsLoading = false;
        state.subscriptionsError = action.payload as string;
      });

    // Fetch stats
    builder
      .addCase(fetchAcademyStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchAcademyStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchAcademyStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload as string;
      });

    // Generate payment link
    builder
      .addCase(generateAcademyPaymentLink.pending, (state) => {
        state.paymentLinkLoading = true;
        state.paymentLinkError = null;
      })
      .addCase(generateAcademyPaymentLink.fulfilled, (state, action) => {
        state.paymentLinkLoading = false;
        state.paymentLink = action.payload;
      })
      .addCase(generateAcademyPaymentLink.rejected, (state, action) => {
        state.paymentLinkLoading = false;
        state.paymentLinkError = action.payload as string;
      });

    // Calculate upgrade
    builder
      .addCase(calculateAcademyUpgrade.pending, (state) => {
        state.calculationLoading = true;
        state.calculationError = null;
      })
      .addCase(calculateAcademyUpgrade.fulfilled, (state, action) => {
        state.calculationLoading = false;
        state.upgradeCalculation = action.payload;
      })
      .addCase(calculateAcademyUpgrade.rejected, (state, action) => {
        state.calculationLoading = false;
        state.calculationError = action.payload as string;
      });

    // Generate upgrade link
    builder
      .addCase(generateAcademyUpgradeLink.pending, (state) => {
        state.upgradePaymentLinkLoading = true;
        state.upgradePaymentLinkError = null;
      })
      .addCase(generateAcademyUpgradeLink.fulfilled, (state, action) => {
        state.upgradePaymentLinkLoading = false;
        state.upgradePaymentLink = action.payload;
      })
      .addCase(generateAcademyUpgradeLink.rejected, (state, action) => {
        state.upgradePaymentLinkLoading = false;
        state.upgradePaymentLinkError = action.payload as string;
      });
  },
});

export const {
  clearPaymentLink,
  clearUpgradeCalculation,
  clearUpgradePaymentLink,
  resetAcademyState,
} = academySlice.actions;

export default academySlice.reducer;
